import React, { useState } from 'react';
import {
  IAvailableWorkflowActions,
  ISubmissionFileSet
} from '@publishing-engine/domain/models';
import { Flex } from '@chakra-ui/react';
import { FormFieldFiguresAndTables } from '@publishing-engine/domain/types/article';
import { FieldHeader } from '../common/FieldHeader';
import { FiguresAndTablesDiffModal } from './FileListDiffModal';
import FiguresAndTablesUpdateModal from './FiguresAndTablesUpdateModal';
import { FiguresAndTablesEmptyState } from './FiguresAndTableEmptyState';
import { FiguresAndTablesDisplay } from './FiguresAndTableDisplay';
import { EntryPoint } from 'src/utils/types';
import { useSubmissionFields } from 'src/hooks/useSubmissionFields';

interface Props {
  formFieldDefinition: FormFieldFiguresAndTables;
  value: ISubmissionFileSet['FiguresAndTables'];
  previousValue?: ISubmissionFileSet['FiguresAndTables'];
  isEditable: boolean;
  onChange: (value: ISubmissionFileSet['FiguresAndTables']) => void;
  submissionContextId: string;
  availableActions: IAvailableWorkflowActions;
  entryPoint: EntryPoint;
  isUpdateRequired?: boolean;
}

export const FiguresAndTables: React.FC<Props> = ({
  formFieldDefinition,
  value,
  previousValue,
  isEditable,
  submissionContextId,
  availableActions,
  onChange,
  entryPoint,
  isUpdateRequired
}) => {
  const { checkFormFieldValueIsUpdated } = useSubmissionFields(entryPoint);
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const isUpdated = checkFormFieldValueIsUpdated(formFieldDefinition.name);

  const editCallback = isEditable
    ? () => {
        setIsEditing(true);
      }
    : undefined;

  const [showDiffModal, setShowDiffModal] = useState<boolean>(false);
  const openDiffModal = () => setShowDiffModal(true);
  const closeDiffModal = () => setShowDiffModal(false);
  const hasValue = !!value?.exists;

  return (
    <Flex flexDirection={'column'} gap={2}>
      {isEditing && (
        <FiguresAndTablesUpdateModal
          isOpen={isEditing}
          formField={formFieldDefinition}
          onClose={() => setIsEditing(false)}
          value={value}
          onSubmit={value => {
            setIsEditing(false);
            onChange(value.fileSet.FiguresAndTables);
          }}
          submissionContextId={submissionContextId}
        />
      )}

      {isUpdated && showDiffModal ? (
        <FiguresAndTablesDiffModal
          config={formFieldDefinition}
          value={value}
          previousValue={previousValue}
          onClose={closeDiffModal}
        />
      ) : null}

      <FieldHeader
        {...formFieldDefinition}
        label={formFieldDefinition.params.labels.main}
        subtitle={formFieldDefinition.params.labels.subtitle}
        isUpdated={isUpdated}
        editCallback={editCallback}
        onShowDiffModal={openDiffModal}
        availableActions={availableActions}
        entryPoint={entryPoint}
        isUpdateRequired={isUpdateRequired}
      />

      {hasValue ? (
        <FiguresAndTablesDisplay
          value={value.value}
          fieldConfig={formFieldDefinition}
        />
      ) : (
        <FiguresAndTablesEmptyState
          fieldLabels={formFieldDefinition.params.labels}
        />
      )}
    </Flex>
  );
};
